/**
 * RoleSwitcher Component
 *
 * A dropdown component that allows Platform Admins to assume different user roles.
 * Only renders for users with the platform_admin role.
 * When assuming org-based roles, allows selecting which organization to view as.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption, ROLE_LABELS, ORG_REQUIRED_ROLES } from '../../context/RoleAssumptionContext';
import { UserRole, Organization, getAllOrganizations } from '../../services/firestore';
import './RoleSwitcher.css';

const ALL_ROLES: UserRole[] = [
  'platform_admin',
  'cra_admin',
  'cra_analyst',
  'rating_committee',
  'bank_admin',
  'bank_user',
];

// Map roles to the org types they can belong to
const ROLE_ORG_TYPES: Record<UserRole, 'cra' | 'bank' | null> = {
  platform_admin: null,
  cra_admin: 'cra',
  cra_analyst: 'cra',
  rating_committee: 'cra',
  bank_admin: 'bank',
  bank_user: 'bank',
};

export default function RoleSwitcher() {
  const { isPlatformAdmin } = useAuth();
  const {
    canAssumeRoles,
    effectiveRole,
    isAssuming,
    assumeRole,
    exitAssumedRole,
    assumedOrganization,
  } = useRoleAssumption();

  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Only render for platform admins
  if (!isPlatformAdmin || !canAssumeRoles) {
    return null;
  }

  // Fetch organizations when opening org selector
  useEffect(() => {
    if (showOrgSelector && organizations.length === 0) {
      setLoadingOrgs(true);
      getAllOrganizations()
        .then(orgs => setOrganizations(orgs))
        .catch(err => console.error('Failed to load organizations:', err))
        .finally(() => setLoadingOrgs(false));
    }
  }, [showOrgSelector, organizations.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowOrgSelector(false);
        setSelectedRole(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowOrgSelector(false);
        setSelectedRole(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleRoleSelect = (role: UserRole) => {
    // If this role requires an org, show the org selector
    if (ORG_REQUIRED_ROLES.includes(role)) {
      setSelectedRole(role);
      setShowOrgSelector(true);
    } else {
      // Platform admin - no org needed
      assumeRole(role);
      setIsOpen(false);
      setShowOrgSelector(false);
      setSelectedRole(null);
    }
  };

  const handleOrgSelect = (org: Organization) => {
    if (selectedRole) {
      assumeRole(selectedRole, org);
      setIsOpen(false);
      setShowOrgSelector(false);
      setSelectedRole(null);
    }
  };

  const handleBackToRoles = () => {
    setShowOrgSelector(false);
    setSelectedRole(null);
  };

  const handleExit = () => {
    exitAssumedRole();
    setIsOpen(false);
    setShowOrgSelector(false);
    setSelectedRole(null);
  };

  // Filter organizations based on selected role's required type
  const filteredOrgs = selectedRole
    ? organizations.filter(org => org.type === ROLE_ORG_TYPES[selectedRole])
    : organizations;

  // Build the display text for trigger button
  const getTriggerText = () => {
    if (isAssuming && assumedOrganization) {
      return `${t(ROLE_LABELS[effectiveRole!])} @ ${assumedOrganization.name}`;
    }
    return effectiveRole ? t(ROLE_LABELS[effectiveRole]) : t('roleSwitcher.none');
  };

  return (
    <div className="role-switcher" ref={dropdownRef}>
      <button
        className={`role-switcher-trigger ${isAssuming ? 'assuming' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg
          className="role-switcher-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span className="role-switcher-label">
          {isAssuming ? t('roleSwitcher.viewingAs') : t('roleSwitcher.switchRole')}
        </span>
        <span className="role-switcher-role">
          {getTriggerText()}
        </span>
        <svg
          className={`role-switcher-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="role-switcher-dropdown" role="listbox">
          {/* Role Selection View */}
          {!showOrgSelector && (
            <>
              <div className="role-switcher-header">{t('roleSwitcher.selectRole')}</div>
              {ALL_ROLES.map((role) => (
                <button
                  key={role}
                  className={`role-switcher-option ${role === effectiveRole && !ORG_REQUIRED_ROLES.includes(role) ? 'active' : ''}`}
                  onClick={() => handleRoleSelect(role)}
                  role="option"
                  aria-selected={role === effectiveRole}
                >
                  <span className="option-label">{t(ROLE_LABELS[role])}</span>
                  {ORG_REQUIRED_ROLES.includes(role) && (
                    <svg
                      className="option-arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  )}
                  {role === effectiveRole && !ORG_REQUIRED_ROLES.includes(role) && (
                    <svg
                      className="option-check"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
              {isAssuming && (
                <>
                  <div className="role-switcher-divider" />
                  <button className="role-switcher-exit" onClick={handleExit}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {t('roleSwitcher.exitRoleAssumption')}
                  </button>
                </>
              )}
            </>
          )}

          {/* Organization Selection View */}
          {showOrgSelector && (
            <>
              <button className="role-switcher-back" onClick={handleBackToRoles}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                {t('roleSwitcher.backToRoles')}
              </button>
              <div className="role-switcher-header">
                {t('roleSwitcher.selectOrgFor', { role: selectedRole ? t(ROLE_LABELS[selectedRole]) : '' })}
              </div>
              {loadingOrgs ? (
                <div className="role-switcher-loading">{t('roleSwitcher.loadingOrgs')}</div>
              ) : filteredOrgs.length === 0 ? (
                <div className="role-switcher-empty">
                  {t('roleSwitcher.noOrgsFound', { type: selectedRole && ROLE_ORG_TYPES[selectedRole] === 'cra' ? 'CRA' : 'Bank' })}
                </div>
              ) : (
                filteredOrgs.map((org) => (
                  <button
                    key={org.id}
                    className={`role-switcher-option ${assumedOrganization?.id === org.id && effectiveRole === selectedRole ? 'active' : ''}`}
                    onClick={() => handleOrgSelect(org)}
                    role="option"
                  >
                    <div className="option-org-info">
                      <span className="option-label">{org.name}</span>
                      <span className="option-org-type">{org.type.toUpperCase()}</span>
                    </div>
                    {assumedOrganization?.id === org.id && effectiveRole === selectedRole && (
                      <svg
                        className="option-check"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
